import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, ToastMessageService } from '@kw-shared/common';
import { TranslateService } from '@ngx-translate/core';
import { BrowsingCallbackDataAdded } from 'apps/keba-wallbox-app/src/plugins/keba-wallbox-plugin';
import { IAppEnvironment } from '../../../environments/IAppEnvironment';

export enum LocalErrorIdentifier {
  ACTION_CANCELED = 'ACTION_CANCELED',
  AUTHENTICATION_BLOCKED = 'AUTHENTICATION_BLOCKED',
  AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
  BLE_DEVICE_NOT_FOUND = 'BLE_DEVICE_NOT_FOUND',
  BLE_UNSUPPORTED = 'BLE_UNSUPPORTED',
  BLE_UNAUTHORIZED = 'BLE_UNAUTHORIZED',
  BLE_POWERED_OFF = 'BLE_POWERED_OFF',
  BLE_TIMEOUT = 'BLE_TIMEOUT',
  BLE_CONNECTION_FAILED = 'BLE_CONNECTION_FAILED',
  BLE_PAIRING_FAILED = 'BLE_PAIRING_FAILED',
  REST_MISSING_PERMISSION = 'REST_MISSING_PERMISSION',
  REST_TOKEN_INVALID = 'REST_TOKEN_INVALID',
  NO_NETWORK_ACCESS = 'NO_NETWORK_ACCESS',
  BLE_PEER_REMOVED_PAIRING_INFORMATION = 'BLE_PEER_REMOVED_PAIRING_INFORMATION',
  REST_CERTIFICATE_INVALID = 'REST_CERTIFICATE_INVALID',
  BLE_NOT_IN_INSTALLER_MODE = 'BLE_NOT_IN_INSTALLER_MODE',
  VALIDATION_RANGE_MAX_FAILURE = 'VALIDATION_RANGE_MAX_FAILURE',
  FILE_INVALID = 'FILE_INVALID',
  REST_CREDENTIALS_INVALID = 'REST_CREDENTIALS_INVALID'
}

export type LocalError = {
  title: string;
  message: string;
  identifier?: LocalErrorIdentifier;
};

@Injectable({
  providedIn: 'root',
})
export class LocalErrorHandler {
  private errorCounts: Map<string, number> = new Map();

  constructor(
    @Inject(ENVIRONMENT) private readonly environment: IAppEnvironment,
    private readonly toastMessageService: ToastMessageService,
    private readonly translateService: TranslateService
  ) {}

  public handleWallboxRequestErrorAsToastMessage(
    methodName: string,
    error: LocalError,
    showInProduction?: boolean
  ): void {
    if (error) {
      this.toastMessageService.setError(this.handleError(methodName, error).message, showInProduction);
    }
  }

  public handleWallboxRequestErrorAsInlineMessage(methodName: string, error: LocalError): LocalError | undefined {
    if (error) {
      return this.handleError(methodName, error);
    }
    return;
  }

  public handleBlePairingFailed(wallbox: BrowsingCallbackDataAdded): boolean {
    const serialNumber = wallbox.serialNumber;
    const errorCount = this.errorCounts.get(serialNumber) || 0;
    this.errorCounts.set(serialNumber, errorCount + 1);
    // Trigger dialog if error count reaches threshold
    return errorCount + 1 >= 2;
  }

  private handleError(methodName: string, error: LocalError): LocalError {
    // action is defined in error object -> custom error
    const errorMessage = [];
    let title: string = '';

    // if (!this.environment.production) {
    //   // method name as prefix for dev and qa errors
    //   errorMessage.push(methodName + ': ' + error);
    // }

    let identifier: LocalErrorIdentifier | undefined;
    if (error.message) {
      const m = error.message.split('.');
      if (m.length === 2) {
        identifier = m[1] as LocalErrorIdentifier;
        const translatedIdentifier = this.handleIdentifier(identifier);
        if (translatedIdentifier) {
          errorMessage.push(translatedIdentifier?.message);
          title = translatedIdentifier?.title;
        }
      }
    }

    return { title, message: errorMessage.join('\n'), identifier };
  }

  private handleIdentifier(errorIdentifier: LocalErrorIdentifier): LocalError | undefined {
    if (errorIdentifier in LocalErrorIdentifier) {
      // action is already defined
      let translationKey = 'kwa.local-error.' + errorIdentifier.toLowerCase();
      return {
        title: this.translateService.instant(translationKey + '.title'),
        message: this.translateService.instant(translationKey + '.message'),
      };
    }

    // action is not defined - add translation key!
    return undefined;
  }
}
